import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, User, Mail, Phone, Building2, DollarSign, FileText, Calendar } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { api } from '../services/api';
import { DocumentViewer } from '../components/documents/DocumentViewer';
import { Investor } from '../types';
import { formatDate, formatDateSpanishLong } from '../utils/date';

const parseNum = (v: unknown): number => (typeof v === 'number' && !Number.isNaN(v) ? v : Number(v) || 0);

export const KYCDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'general' | 'investments' | 'documents'>('general');
  const [investor, setInvestor] = useState<Investor | null>(null);
  const [investments, setInvestments] = useState<Array<{ id: string; propertyId: string; propertyName?: string; amount: number; ownershipPct: number; status: string; createdAt: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await api.getInvestor(id);
        const invList = data?.investments ?? [];
        setInvestor({
          id: data.id,
          name: data.name ?? '',
          email: data.email ?? '',
          phone: data.phone ?? '',
          status: data.status ?? 'active',
          type: data.type ?? 'fisica',
          totalInvested: parseNum(data.totalInvested),
          propertiesCount: parseNum(data.propertiesCount),
          curp: data.curp,
          csf: data.csf,
          kyc: data.kyc,
          documents: (data.investor_documents ?? []).map((d: any) => ({
            id: d.id,
            title: d.title ?? '',
            fileUrl: d.fileUrl,
            fileType: d.fileType,
            uploadedAt: d.uploadedAt ?? '',
          })),
          createdAt: data.createdAt ?? new Date().toISOString(),
        });
        setInvestments(
          invList.map((inv: any) => ({
            id: inv.id,
            propertyId: inv.propertyId,
            propertyName: inv.properties?.name ?? inv.propertyName ?? 'N/A',
            amount: parseNum(inv.amount),
            ownershipPct: parseNum(inv.ownershipPct),
            status: inv.status ?? 'active',
            createdAt: inv.createdAt ?? '',
          }))
        );
      } catch (err: any) {
        setError(err.message ?? 'Error al cargar el inversionista');
        setInvestor(null);
        setInvestments([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const investorInvestments = investments;
  const activeInvestments = investorInvestments.filter((inv) => inv.status === 'active');

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-3">
          <Button variant="ghost" onClick={() => navigate('/kyc')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver
          </Button>
        </div>
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary border-t-transparent" />
        </div>
      </div>
    );
  }

  if (error || !investor) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-3">
          <Button variant="ghost" onClick={() => navigate('/kyc')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver
          </Button>
        </div>
        <Card>
          <div className="text-center py-12">
            <p className="text-gray-500">{error ?? 'Inversionista no encontrado'}</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Button variant="ghost" onClick={() => navigate('/kyc')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver a KYC
          </Button>
          <ShieldCheck className="w-6 h-6 text-primary" />
          <h1 className="text-2xl font-bold text-gray-900">KYC - {investor.name}</h1>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          {[
            { id: 'general', label: 'Información General' },
            { id: 'investments', label: 'Inversiones' },
            { id: 'documents', label: 'Documentos' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-accent text-accent'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab: Información General */}
      {activeTab === 'general' && (
        <div className="space-y-6">
          <Card>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <User className="w-5 h-5 mr-2 text-primary" />
                  Información Personal
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Nombre Completo</label>
                    <p className="mt-1 text-sm text-gray-900">{investor.name}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Email</label>
                    <p className="mt-1 text-sm text-gray-900 flex items-center">
                      <Mail className="w-4 h-4 mr-2 text-gray-400" />
                      {investor.email}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Teléfono</label>
                    <p className="mt-1 text-sm text-gray-900 flex items-center">
                      <Phone className="w-4 h-4 mr-2 text-gray-400" />
                      {investor.phone}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Tipo</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {investor.type === 'fisica' ? 'Persona Física' : investor.type === 'moral' ? 'Persona Moral' : '-'}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Estado</label>
                    <p className="mt-1">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        investor.status === 'active'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {investor.status === 'active' ? 'Activo' : 'Inactivo'}
                      </span>
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Fecha de Registro</label>
                    <p className="mt-1 text-sm text-gray-900 flex items-center">
                      <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                      {formatDateSpanishLong(investor.createdAt)}
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <DollarSign className="w-5 h-5 mr-2 text-primary" />
                  Resumen Financiero
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total Invertido</label>
                    <p className="mt-1 text-2xl font-bold text-gray-900">
                      ${parseNum(investor.totalInvested).toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Número de Propiedades</label>
                    <p className="mt-1 text-2xl font-bold text-gray-900 flex items-center">
                      <Building2 className="w-5 h-5 mr-2 text-primary" />
                      {investor.propertiesCount}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Inversiones Activas</label>
                    <p className="mt-1 text-2xl font-bold text-green-600">
                      {activeInvestments.length}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Tab: Inversiones */}
      {activeTab === 'investments' && (
        <div className="space-y-6">
          <Card>
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Inversiones</h3>
            </div>
            <div className="overflow-x-auto">
              {investorInvestments.length > 0 ? (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Propiedad
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Monto
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Porcentaje
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Fecha
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Estado
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {investorInvestments.map((investment) => (
                        <tr key={investment.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {investment.propertyName ?? 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            ${investment.amount.toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {investment.ownershipPct}%
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(investment.createdAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              investment.status === 'active'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {investment.status === 'active' ? 'Activa' : 'Inactiva'}
                            </span>
                          </td>
                        </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No hay inversiones registradas
                </div>
              )}
            </div>
          </Card>
        </div>
      )}

      {/* Tab: Documentos */}
      {activeTab === 'documents' && (
        <div className="space-y-6">
          <Card>
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <FileText className="w-5 h-5 mr-2 text-primary" />
                Documentos
              </h3>
            </div>
            {investor.documents && investor.documents.length > 0 ? (
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {investor.documents
                    .filter((d) => !!d.fileUrl)
                    .map((document) => (
                      <DocumentViewer
                        key={document.id}
                        title={document.title}
                        fileUrl={document.fileUrl!}
                        fileTypeLabel={formatDate(document.uploadedAt)}
                        downloadName={document.title || undefined}
                      />
                    ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500">No hay documentos adjuntos</p>
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
};
